import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { getReviewerOptions } from '../../libs/api/pipelineService';
import type { PipelineCard } from './kanbanShared';

interface HrApproveModalProps {
  /** Card being moved to HR Approved; null closes the modal. */
  card: Pick<PipelineCard, 'applicationId' | 'name' | 'role'> | null;
  /** Talent Pool move to another JD: suggest that JD's reviewers. */
  targetJd?: { id: string; title: string } | null;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (reviewerEmails: string[]) => void;
}

/**
 * NEW -> HR Approved: HR picks who reviews the CV (from the CV review rules). The best match for
 * the JD is preselected; more reviewers can be added. Each picked reviewer is notified, and the
 * first evaluation decides the CV.
 */
export const HrApproveModal: React.FC<HrApproveModalProps> = ({
  card,
  targetJd,
  submitting,
  onCancel,
  onConfirm,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const { data, isLoading } = useQuery({
    queryKey: ['reviewer-options', card?.applicationId, targetJd?.id],
    queryFn: () => getReviewerOptions(card!.applicationId, targetJd?.id),
    enabled: card !== null,
    // A background refetch would reset HR's ticks to the suggestion (see the effect below).
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Start from the suggested reviewer(s) each time the modal opens for a card.
  useEffect(() => {
    setSelected(card && data ? data.default_emails : []);
  }, [card, data]);

  const toggle = (email: string, checked: boolean) =>
    setSelected((prev) =>
      checked ? [...new Set([...prev, email])] : prev.filter((item) => item !== email),
    );

  const matching = (data?.items ?? []).filter((item) => item.covers_jd);
  const others = (data?.items ?? []).filter((item) => !item.covers_jd);

  const handleOpenChange = (open: boolean) => {
    if (open || submitting) return;
    onCancel();
  };

  const renderGroup = (title: string, items: typeof matching) =>
    items.length > 0 ? (
      <div className="space-y-1.5">
        <div className="text-[11px] font-bold uppercase tracking-wide text-text-subtle">
          {title}
        </div>
        {items.map((item) => (
          <label
            key={item.email}
            className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm transition-colors ${
              selected.includes(item.email)
                ? 'border-primary/60 bg-primary-light/60'
                : 'border-border-subtle bg-bg-main/40 hover:border-primary/40'
            }`}
          >
            <Checkbox
              checked={selected.includes(item.email)}
              onCheckedChange={(checked) => toggle(item.email, checked === true)}
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-text-main">{item.name}</span>
              <span className="block truncate text-xs text-text-subtle">{item.email}</span>
            </span>
          </label>
        ))}
      </div>
    ) : null;

  return (
    <Dialog open={card !== null} onOpenChange={handleOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-md">
        <DialogHeader className="border-b border-border-subtle p-5">
          <DialogTitle className="flex items-center gap-2 text-text-main">
            <span className="size-2 rounded-full bg-info" />
            Move to HR Approved
          </DialogTitle>
          <DialogDescription className="text-text-subtle">
            {card ? `${card.name} · ${targetJd?.title ?? card.role}` : null}
            <span className="mt-1 block">
              Pick who reviews this CV. They are notified and the first evaluation decides it.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-96 space-y-4 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-text-subtle">
              <Loader2 size={14} className="animate-spin" /> Loading reviewers...
            </div>
          ) : (
            <>
              {renderGroup('Reviewers of this JD', matching)}
              {renderGroup('Other reviewers', others)}
              {data && data.items.length === 0 ? (
                <div className="text-sm text-text-subtle">No CV reviewers are configured yet.</div>
              ) : null}
            </>
          )}
        </div>

        <DialogFooter className="border-t border-border-subtle p-4">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={selected.length === 0 || submitting}
            onClick={() => onConfirm(selected)}
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
            Approve & notify ({selected.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
